import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Knowledge → Library: the empire-kb doc store, native in the Monarch shell.
// List/search → doc detail (canonical body + badge-distinct store overlays),
// propose-an-edit for everyone, policy acknowledgment, own-store overlay
// editing for managers. All data flows through Monarch's /api/kb proxy — the
// KB renders body_html server-side (sanitized), so this view never parses
// markdown.

const CLASS_COLORS = {
  standard:  { bg: '#E8F5E9', fg: '#2D7A3A' },
  policy:    { bg: '#FBEAE5', fg: '#B4532A' },
  training:  { bg: '#EAEAF6', fg: '#4C4C9D' },
  reference: { bg: '#f5e9d6', fg: '#8a5a1a' },
};

function ClassBadge({ cls }) {
  const c = CLASS_COLORS[cls] || CLASS_COLORS.reference;
  return <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
    padding: '2px 7px', borderRadius: 4, marginLeft: 6, background: c.bg, color: c.fg }}>{cls}</span>;
}

export function KbLibraryView({ user, isManager }) {
  const headers = { 'x-user-id': String(user.id) };
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null); // slug
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [proposing, setProposing] = useState(false);
  const [proposal, setProposal] = useState({ body: '', rationale: '' });
  const [overlayDraft, setOverlayDraft] = useState(null);

  async function load(query) {
    try {
      const url = query ? `/api/kb/docs?q=${encodeURIComponent(query)}` : '/api/kb/docs';
      setDocs(await api('GET', url, null, headers));
    } catch (e) { setError(e.message || 'Failed to load documents'); }
  }
  useEffect(() => { load(''); }, []);

  async function openDoc(slug) {
    setSelected(slug); setDoc(null); setFlash(''); setProposing(false); setOverlayDraft(null);
    try { setDoc(await api('GET', `/api/kb/docs/${encodeURIComponent(slug)}`, null, headers)); }
    catch (e) { setError(e.message || 'Failed to load document'); }
  }

  async function submitProposal() {
    try {
      await api('POST', `/api/kb/docs/${encodeURIComponent(selected)}/proposals`,
        { proposed_body_md: proposal.body, rationale: proposal.rationale }, headers);
      setProposing(false);
      setFlash('Proposal submitted — it routes to the doc owner, and you will hear back either way.');
    } catch (e) { alert(e.message || 'Proposal failed'); }
  }

  async function acknowledge() {
    try {
      await api('POST', `/api/kb/docs/${encodeURIComponent(selected)}/ack`, {}, headers);
      setFlash('Acknowledged — recorded with version and timestamp.');
      setDoc({ ...doc, acked: true });
    } catch (e) { alert(e.message || 'Acknowledge failed'); }
  }

  async function saveOverlay() {
    try {
      await api('PUT', `/api/kb/docs/${encodeURIComponent(selected)}/overlay`, { body_md: overlayDraft }, headers);
      setOverlayDraft(null);
      openDoc(selected);
    } catch (e) { alert(e.message || 'Overlay save failed'); }
  }

  if (error) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
    <div className="text-xs text-red-700">{error}</div></div>;

  // ── Detail view ──────────────────────────────────────────────────────────
  if (selected) {
    if (!doc) return <div className="card">Loading…</div>;
    const myOverlay = doc.overlays.find((o) => o.location === doc.viewer_location);
    return (
      <div className="space-y-3">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn" onClick={() => { setSelected(null); setDoc(null); load(q); }}>← Library</button>
          {doc.doc_class === 'policy' && (doc.acked
            ? <button className="btn" disabled>✓ Acknowledged</button>
            : <button className="btn btn-primary" onClick={acknowledge}>Acknowledge this policy (v{doc.version_num})</button>)}
          {isManager && doc.viewer_location && (
            <button className="btn" onClick={() => setOverlayDraft(myOverlay ? myOverlay.body_md : '')}>
              {myOverlay ? `Edit ${doc.viewer_location} note` : `Add ${doc.viewer_location} note`}
            </button>
          )}
        </div>
        {flash && <div className="card" style={{ borderLeft: '4px solid var(--sage, #34A04A)', fontSize: 13 }}>{flash}</div>}
        <div className="card">
          <div className="label-uppercase" style={{ color: 'var(--gold-text, #8a5a1a)' }}>{doc.doc_class} · canonical</div>
          <div className="serif" style={{ fontSize: 24, fontWeight: 700, margin: '2px 0 2px' }}>{doc.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
            {doc.owner_role} · v{doc.version_num} · {doc.change_note}
          </div>
          <div className="kb-doc-body" dangerouslySetInnerHTML={{ __html: doc.body_html }} />
          {doc.overlays.map((o) => (
            <div key={o.location} style={{ borderLeft: '3px solid var(--sage, #34A04A)', background: '#E8F5E9',
              borderRadius: '0 9px 9px 0', padding: '10px 14px', margin: '12px 0', fontSize: 13.5 }}>
              <div className="label-uppercase" style={{ color: '#2D7A3A', marginBottom: 4 }}>
                {o.location} note{o.location === doc.viewer_location ? ' · your store' : ''}
              </div>
              <div dangerouslySetInnerHTML={{ __html: o.body_html }} />
            </div>
          ))}
        </div>
        {overlayDraft !== null && (
          <div className="card">
            <div className="text-base font-semibold" style={{ marginBottom: 6 }}>{doc.viewer_location} overlay note</div>
            <textarea value={overlayDraft} onChange={(e) => setOverlayDraft(e.target.value)}
              style={{ width: '100%', minHeight: 120, fontSize: 13, padding: 8 }} />
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={saveOverlay}>Save overlay</button>
              <button className="btn" onClick={() => setOverlayDraft(null)}>Cancel</button>
            </div>
          </div>
        )}
        <div className="card">
          {!proposing ? (
            <button className="btn" onClick={() => { setProposing(true); setProposal({ body: doc.body_md, rationale: '' }); }}>
              Propose an edit to this document
            </button>
          ) : (
            <div>
              <div className="text-base font-semibold" style={{ marginBottom: 6 }}>Propose an edit</div>
              <textarea value={proposal.body} onChange={(e) => setProposal({ ...proposal, body: e.target.value })}
                style={{ width: '100%', minHeight: 160, fontFamily: 'monospace', fontSize: 12, padding: 8 }} />
              <input value={proposal.rationale} placeholder="Why this change? (required)"
                onChange={(e) => setProposal({ ...proposal, rationale: e.target.value })}
                style={{ width: '100%', marginTop: 8, padding: 8, fontSize: 13 }} />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" disabled={!proposal.rationale.trim()} onClick={submitProposal}>Submit proposal</button>
                <button className="btn" onClick={() => setProposing(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────
  // Grouped by category when browsing (order/labels come from the KB API);
  // flat when searching so a short hit list isn't broken up by headers.
  const DocCard = ({ d }) => (
    <div className="card" onClick={() => openDoc(d.slug)} style={{ cursor: 'pointer' }}>
      <div style={{ fontSize: 14.5, fontWeight: 600 }}>{d.title}<ClassBadge cls={d.doc_class} /></div>
      {d.summary && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{d.summary}</div>}
    </div>
  );
  let list = null;
  if (docs) {
    if (q) {
      list = docs.map((d) => <DocCard key={d.slug} d={d} />);
    } else {
      list = [];
      let last = '';
      for (const d of docs) {
        if (d.category_label !== last) {
          last = d.category_label;
          list.push(
            <div key={`cat-${d.category_label}`} className="label-uppercase"
              style={{ color: 'var(--gold-text, #8a5a1a)', margin: '14px 0 -4px 2px' }}>
              {d.category_label}
            </div>
          );
        }
        list.push(<DocCard key={d.slug} d={d} />);
      }
    }
  }
  return (
    <div className="space-y-3">
      <input className="card" type="search" placeholder="Search titles, tags, and document text…"
        value={q} style={{ width: '100%', padding: '10px 14px', fontSize: 14 }}
        onChange={(e) => { setQ(e.target.value); load(e.target.value); }} />
      {docs === null && <div className="card">Loading…</div>}
      {list}
      {docs?.length === 0 && <div className="card" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
        {isManager ? 'No matches — try Ask Empire, and the gap gets logged.' : 'No matches — ask a manager.'}</div>}
    </div>
  );
}
